import React from 'react';
import { graphql, useFragment } from 'react-relay';
import { MultiGroupPickerInlineEditViewWithIsEditable } from '@atlassian/jira-issue-field-multi-group-picker-inline-edit-full/src/ui/index.tsx';
import type { multiGroupPicker_nativeIssueTable_MultiGroupPickerCell$key } from '@atlassian/jira-relay/src/__generated__/multiGroupPicker_nativeIssueTable_MultiGroupPickerCell.graphql';
import { INLINE_EDIT_ANALYTICS_ATTRIBUTES } from '../common/inline-edit-analytics-attributes.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';

export type Props = {
	fieldRef: multiGroupPicker_nativeIssueTable_MultiGroupPickerCell$key;
	isReadOnlyWithoutHoverPopover?: boolean;
};
export const MultiGroupPickerCell = ({ fieldRef, isReadOnlyWithoutHoverPopover }: Props) => {
	const data = useFragment<multiGroupPicker_nativeIssueTable_MultiGroupPickerCell$key>(
		graphql`
			fragment multiGroupPicker_nativeIssueTable_MultiGroupPickerCell on JiraMultipleGroupPickerField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				lazyIsEditableInIssueView @include(if: $isInlineEditingEnabled)
				...ui_issueFieldMultiGroupPickerInlineEditFull_MultiGroupPickerInlineEditViewWithIsEditable_fragmentRef
			}
		`,
		fieldRef,
	);
	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	// If the property does not exist in fragment data then we know it has been conditionally excluded because inline
	// editing is disabled. In this case we explicitly set isEditable to false to bypass the lazy editability query.
	const isEditable = 'lazyIsEditableInIssueView' in data ? data.lazyIsEditableInIssueView : false;
	const fieldId = typeof data.id === 'string' ? data.id : '';
	useLazyLoadEditability(fieldId, isEditable);
	// In edit mode, use the MultiGroupPickerEditView
	return (
		<MultiGroupPickerInlineEditViewWithIsEditable
			attributes={INLINE_EDIT_ANALYTICS_ATTRIBUTES}
			spacing="default"
			editViewPopup
			editViewPopupAlignBlock="center"
			fragmentRef={data}
			isEditable={(isEditable && !isReadOnlyWithoutHoverPopover) ?? false}
			menuPosition="fixed"
			readViewFitContainerHeight
			isTruncated
			isTruncatedWithHoverPopover={isSingleLineRowHeightEnabled}
		/>
	);
};
